import { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { CheckCircle2, Loader2, LogIn, AlertTriangle } from 'lucide-react';

/**
 * Landing page for players returning from a payment gateway.
 *
 * URL: /deposit-return?deposit=<order_id>&tab=wallet
 *
 * This route is INTENTIONALLY public (no ProtectedRoute wrapper) because:
 *  1. Some mobile browsers / in-app webviews drop localStorage when
 *     redirecting from a different origin. If the player landed on a
 *     protected route with no session, they got a blank white screen
 *     while ProtectedRoute silently navigated to /auth — losing the
 *     deposit context entirely.
 *  2. Here we always render SOMETHING — either a "processing" spinner
 *     while we kick off the settlement poll, or a friendly sign-in CTA
 *     if the session is truly gone.
 *
 * Flow:
 *  - If token present: call POST /api/deposits/{id}/poll-status to force
 *    settlement, then forward to /app?tab=wallet.
 *  - If no token: show "Session expired — log back in to see your
 *    deposit" with a deep-link that carries the deposit id forward.
 */
export default function DepositReturnPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const depositId = params.get('deposit') || params.get('order');
  const tenantSlug = params.get('tenant') || null;

  const [state, setState] = useState('checking'); // checking | settling | settled | needs_login | error
  const [detail, setDetail] = useState('');
  const [attempts, setAttempts] = useState(0);

  const appPath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const authPath = tenantSlug ? `/${tenantSlug}/auth` : '/auth';

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
    if (!token) {
      setState('needs_login');
      return;
    }

    if (!depositId) {
      // Nothing to do — forward into the wallet tab
      navigate(`${appPath}?tab=wallet`, { replace: true });
      return;
    }

    let cancelled = false;
    let tries = 0;
    const MAX = 20; // 20 × 1.5s = 30s

    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      setAttempts(tries);
      setState('settling');
      try {
        const res = await api.post(`/api/deposits/${depositId}/poll-status`);
        const status = res?.data?.status || res?.data?.gateway_status;
        if (status === 'APPROVED' || status === 'PAID' || status === 'COMPLETED') {
          setState('settled');
          setTimeout(() => navigate(`${appPath}?tab=wallet`, { replace: true }), 1200);
          return;
        }
        if (status === 'FAILED' || status === 'EXPIRED' || status === 'REJECTED') {
          setState('error');
          setDetail(`Payment ${status.toLowerCase()} — please try again.`);
          return;
        }
      } catch (err) {
        // Transient — keep polling unless it's an auth failure
        if (err?.response?.status === 401) {
          setState('needs_login');
          return;
        }
      }
      if (tries >= MAX) {
        // Long tail — send them into the wallet anyway; the background
        // auto-sync will finish settlement within 60s
        setState('settled');
        setDetail('Still processing — we will update your balance as soon as it clears.');
        setTimeout(() => navigate(`${appPath}?tab=wallet&deposit=${depositId}`, { replace: true }), 2000);
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [depositId, navigate, appPath]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#0B0E14' }}
      data-testid="deposit-return-page"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#131822] p-8 shadow-2xl text-center">
        {state === 'settling' && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-[#F5D76E] animate-spin" />
            <h1 className="text-2xl font-bold text-white mt-5">Crediting your wallet…</h1>
            <p className="text-sm text-gray-400 mt-2">
              We're confirming your payment with the gateway.
              This usually takes a few seconds.
            </p>
            <p className="text-xs text-gray-500 mt-3">
              Attempt {attempts} / 20 — please don't close this page.
            </p>
          </>
        )}
        {state === 'settled' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-400" />
            <h1 className="text-2xl font-bold text-white mt-5">All set!</h1>
            <p className="text-sm text-gray-400 mt-2">
              {detail || 'Redirecting you back to your wallet…'}
            </p>
            <Link
              to={`${appPath}?tab=wallet`}
              className="inline-block mt-5 px-5 py-2 rounded-lg font-semibold"
              style={{ background: '#F5D76E', color: '#0B0E14' }}
              data-testid="deposit-return-go-wallet"
            >
              Open Wallet
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <AlertTriangle className="w-14 h-14 mx-auto text-red-400" />
            <h1 className="text-2xl font-bold text-white mt-5">Payment issue</h1>
            <p className="text-sm text-gray-400 mt-2">{detail}</p>
            <Link
              to={`${appPath}?tab=wallet`}
              className="inline-block mt-5 px-5 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition"
              data-testid="deposit-return-back"
            >
              Back to Wallet
            </Link>
          </>
        )}
        {state === 'needs_login' && (
          <>
            <LogIn className="w-14 h-14 mx-auto text-[#F5D76E]" />
            <h1 className="text-2xl font-bold text-white mt-5">Log back in to finish</h1>
            <p className="text-sm text-gray-400 mt-2">
              Your payment {depositId ? 'is being processed' : 'was received'} —
              sign in again to see your updated balance. We'll take you straight to your wallet.
            </p>
            <Link
              to={`${authPath}?next=${encodeURIComponent(appPath + '?tab=wallet' + (depositId ? `&deposit=${depositId}` : ''))}`}
              className="inline-block mt-5 px-6 py-2.5 rounded-lg font-semibold"
              style={{ background: '#F5D76E', color: '#0B0E14' }}
              data-testid="deposit-return-login"
            >
              Sign In
            </Link>
          </>
        )}
        {state === 'checking' && (
          <Loader2 className="w-10 h-10 mx-auto text-gray-500 animate-spin" />
        )}
      </div>
    </div>
  );
}
